import { Badge, Group, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { money } from '@/components/InvoiceDetailsModal/DocumentCard';
import { StatusHeaderBar } from '@/components/StatusHeaderBar/StatusHeaderBar';
import { collectionLevel } from '@/lib/proformaState';

export const STATE_COLOR: Record<string, string> = {
  issued: 'blue',
  paid: 'teal',
  cancelled: 'gray',
};

const LEVEL: Record<string, { label: string; color: string }> = {
  none: { label: 'Not collected', color: 'gray' },
  partly: { label: 'Partly collected', color: 'yellow' },
  fully: { label: 'Fully collected', color: 'teal' },
  overpaid: { label: 'Overpaid', color: 'orange' },
};

/** Collected against due, an indicator next to the state and never a state. */
export function CollectedIndicator({
  collected,
  due,
  currency,
}: {
  collected: number;
  due: number;
  currency: string;
}) {
  const level = LEVEL[collectionLevel(collected, due)];
  return (
    <Badge
      color={level.color}
      variant="light"
      styles={{ root: { maxWidth: 'none' } }}
    >
      {level.label} · {money(collected, currency)} / {money(due, currency)}
    </Badge>
  );
}

/**
 * The page header: the document's number and its state. The state is shown here and nowhere else
 * on the page; what kind of document it is and its version are in the document and its Versions.
 */
export function DocumentHeader({
  number,
  stateLabel,
  stateColor,
  indicators,
  warning,
  action,
  stateNote,
}: {
  number: string;
  stateLabel: string;
  stateColor: string;
  indicators?: ReactNode;
  warning?: ReactNode;
  /** The one action of the header: the offer's Monday opportunity, when there is one to show. */
  action?: ReactNode;
  /** A line under the state, e.g. "Not yet sent". Its room is kept when there is none. */
  stateNote?: string | null;
}) {
  return (
    <StatusHeaderBar
      statusLabel={stateLabel}
      color={stateColor}
      description={
        <Text
          fz="sm"
          ta="right"
          style={{ visibility: stateNote ? 'visible' : 'hidden' }}
        >
          {stateNote || '-'}
        </Text>
      }
      title={
        <Text fz="xl" fw={700}>
          {number}
        </Text>
      }
      subtitles={
        (indicators || warning) && (
          <Stack gap={4} mt={4}>
            {indicators && (
              <Group gap={8} wrap="wrap" align="center">
                {indicators}
              </Group>
            )}
            {warning}
          </Stack>
        )
      }
      actions={action}
    />
  );
}
